/**
 * Error Boundary component
 * Catches and displays React errors gracefully
 */

import React, { Component, ReactNode, ErrorInfo } from 'react';
import Alert from '@amzn/meridian/alert';
import Button from '@amzn/meridian/button';

interface Props {
  children: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      error,
      errorInfo: null,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    this.props.onError?.(error, errorInfo);
    this.setState({
      error,
      errorInfo,
    });
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  render() {
    if (this.state.hasError) {
      return (
        <div style={{ padding: '40px', maxWidth: '800px', margin: '0 auto' }}>
          <Alert type="error" size="large">
            <h2 style={{ marginTop: 0 }}>Something went wrong</h2>
            <p>
              An unexpected error occurred. Please try refreshing the page or contact support if
              the problem persists.
            </p>
            {this.state.error && (
              <details style={{ marginTop: '20px', whiteSpace: 'pre-wrap' }}>
                <summary style={{ cursor: 'pointer', fontWeight: 'bold' }}>
                  Error Details
                </summary>
                <div style={{ marginTop: '10px', fontSize: '14px', fontFamily: 'monospace' }}>
                  <strong>Error:</strong> {this.state.error.toString()}
                  <br />
                  <br />
                  {this.state.errorInfo && (
                    <>
                      <strong>Component Stack:</strong>
                      <pre style={{ fontSize: '12px', overflow: 'auto' }}>
                        {this.state.errorInfo.componentStack}
                      </pre>
                    </>
                  )}
                </div>
              </details>
            )}
            <div style={{ marginTop: '20px', display: 'flex', gap: '10px' }}>
              <Button onClick={this.handleReset}>
                Try Again
              </Button>
              <Button onClick={() => window.location.reload()}>
                Reload Page
              </Button>
            </div>
          </Alert>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
